import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';

import Colours from '../definitions/Colours';
import Sizes from '../definitions/Sizes';
import MessagePropType from '../definitions/MessagePropType';
import renderMessage from '../functions/renderMessage';
import getBorderColourClassName from '../functions/getBorderColourClassName';
import QuestionMarkButton from '../components/QuestionMarkButton';


function BigNumberBox({
    message,
    subMessage,
    otherMessages,

    onClick,
    isHelpButtonWithOtherMessages = false,

    variant = VARIANT_NONE,
    borderColour,

    children,
    className,
    ...otherProps
}) {
    const intl = useIntl();

    return (
        <Container
            className={classNames(
                'rh-display-flex', 'rh-flex-direction-column', 'rh-position-relative', 'rh-pt-0_5', 'rh-mb-0_5',
                'rh-align-items-center', 'rh-border-width-1px', 'rh-border-style-solid', 'rh-border-radius-8px',
                borderColour
                    ? getBorderColourClassName(borderColour)
                    : 'rh-border-color-stone',
                className,
            )}
            {...otherProps}
        >
            <If condition={variant !== VARIANT_NONE}>
                <p className="outline-label rh-top-0 rh-m-0 rh-text-xs rh-position-absolute rh-text-lowercase rh-bg-coconut rh-px-0_75">
                    {intl.formatMessage(MESSAGES[variant], intl)}
                </p>
            </If>

            <If condition={onClick && !isHelpButtonWithOtherMessages}>
                <QuestionMarkButton
                    className="question-mark-button rh-position-absolute"
                    buttonSize={Sizes.ICONS['XS']}
                    onClick={onClick}
                />
            </If>
            <span className="rh-display-flex rh-flex-direction-column rh-text-align-center">
                <output
                    className="big-number rh-text-l weight-bold"
                    {...renderMessage(message, intl)}
                />
                <If condition={subMessage}>
                    <span className="reduce-spacing-above rh-text-xs">
                        {subMessage}
                    </span>
                </If>
            </span>

            <If condition={otherMessages}>
                <div
                    className={classNames(
                        'other-messages', 'rh-width-100p', 'rh-display-flex', 'rh-align-items-center', 'rh-mt-0_5',
                        'rh-pt-0_75', 'rh-pb-0_5', 'rh-px-0_5', 'rh-box-sizing-border-box', 'rh-bg-stone-lightest', {
                            'rh-flex-direction-column': !isHelpButtonWithOtherMessages,
                        }
                    )}
                >
                    <div className="rh-display-flex rh-flex-grow-1 rh-align-items-center rh-flex-direction-column">
                        <For
                            each="row"
                            of={otherMessages}
                            index="index"
                        >
                            <span
                                key={index}
                                className={classNames('rh-text-xs', 'rh-m-0', 'rh-text-align-center', row.className)}

                                data-test-name="box-other-message"
                                {...renderMessage(row.message, intl)}
                            />
                        </For>
                    </div>
                    <If condition={onClick && isHelpButtonWithOtherMessages}>
                        <QuestionMarkButton
                            className="question-mark-button"
                            buttonSize={Sizes.ICONS['XS']}
                            onClick={onClick}
                        />
                    </If>
                </div>
            </If>

            {children}
        </Container>
    );
}

const VARIANT_NONE = 'none';
const VARIANT_FEATURED = 'featured';
const VARIANT_SPONSORED = 'sponsored';

BigNumberBox.propTypes = {
    message: MessagePropType.isRequired,
    subMessage: PropTypes.node,
    otherMessages: PropTypes.arrayOf(
        PropTypes.shape({
            message: MessagePropType.isRequired,
            className: PropTypes.string,
        }),
    ),

    onClick: PropTypes.func,
    isHelpButtonWithOtherMessages: PropTypes.bool,

    variant: PropTypes.oneOf([
        VARIANT_NONE,
        VARIANT_FEATURED,
        VARIANT_SPONSORED,
    ]),
    borderColour: PropTypes.oneOf(Object.values(Colours)),

    children: PropTypes.node,
    className: PropTypes.string,
};


const Container = styled.div`
    > .outline-label {
        left: 50%;
        transform:
            translateY(-50%)
            translateX(-50%);
    }

    > .question-mark-button {
        top: ${Sizes.SPACING.HALF};
        right: ${Sizes.SPACING.HALF};
    }

    .reduce-spacing-above {
        margin-top: -${Sizes.SPACING.HALF};
    }

    > .other-messages {
        border-radius: 0 0 7px 7px;
    }
`;


const MESSAGES = defineMessages({
    [VARIANT_FEATURED]: {
        id: 'base-ui.BigNumberBox.outlineLabelFeatured',
        defaultMessage: 'Featured',
    },
    [VARIANT_SPONSORED]: {
        id: 'base-ui.BigNumberBox.outlineLabelSponsored',
        defaultMessage: 'Sponsored',
    },
});

export default BigNumberBox;
